// react component named ProfileForm
import React, { useState, useEffect } from 'react';
import './App.css';
import './index.css';

import Titlebar from './Titlebar';
import useUserStore from './UserStore';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { apiUrl } from './config';

const AVAILABLE_ROLES = ['Sales', 'Production', 'Admin'];
const AVAILABLE_FUNCTIONAL_AREAS = ['Dashboard', 'Billboard', 'Orders', 'Invoices', 'Customers', 'Spots', 'Logs', 'Packages', 'Manual Upload', 'Admin', 'Profile'];

function TagEditor({ label, items, availableOptions, onChange, readOnly }) {
    const [inputValue, setInputValue] = useState('');

    const addFromSelect = (value) => {
        if (value && !items.includes(value)) onChange([...items, value]);
    };

    const addCustom = () => {
        const t = inputValue.trim();
        if (t && !items.includes(t)) onChange([...items, t]);
        setInputValue('');
    };

    const remove = (tag) => onChange(items.filter((t) => t !== tag));

    const unselected = availableOptions.filter((o) => !items.includes(o));

    return (
        <div>
            <div className="flex flex-wrap gap-1 mb-2">
                {items.length === 0 && (
                    <span className="text-sm text-gray-400 italic">None assigned</span>
                )}
                {items.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-x-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                        {tag}
                        {!readOnly && (
                            <button type="button" onClick={() => remove(tag)} className="ml-1 rounded-sm text-indigo-400 hover:text-indigo-600">
                                <TrashIcon className="h-3 w-3" />
                            </button>
                        )}
                    </span>
                ))}
            </div>
            {!readOnly && (
                <div className="flex items-center gap-2 mt-1">
                    {unselected.length > 0 && (
                        <select value="" onChange={(e) => addFromSelect(e.target.value)}
                            className="block rounded-md border-0 py-1 pl-2 pr-8 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs sm:leading-6">
                            <option value="" disabled>+ Add {label}</option>
                            {unselected.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    )}
                    <div className="flex items-center gap-1">
                        <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustom())}
                            placeholder={`Custom ${label}…`}
                            className="block rounded-md border-0 py-1 px-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs sm:leading-6" />
                        <button type="button" onClick={addCustom} disabled={!inputValue.trim()}
                            className="rounded-md bg-indigo-600 p-1 text-white shadow-sm hover:bg-indigo-500 disabled:opacity-40">
                            <PlusIcon className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            )}
            {readOnly && (
                <p className="text-xs text-gray-400 italic mt-1">Admin role required to edit.</p>
            )}
        </div>
    );
}

function ProfileForm() {
    const storeUser = useUserStore((state) => state.user);
    const isAdmin = useUserStore((state) =>
        state.user?.roles?.includes('admin') ?? false
    );

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    // Editable fields
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [salesQuota, setSalesQuota] = useState('');
    const [roles, setRoles] = useState([]);
    const [functionalAreas, setFunctionalAreas] = useState([]);

    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const email = storeUser?.authResponse?.profile?.email ?? '';

    useEffect(() => {
        if (!email) {
            setLoading(false);
            return;
        }
        const fetchProfile = async () => {
            setLoading(true);
            setFetchError(null);
            try {
                const res = await fetch(apiUrl(`/profile/email?email=${encodeURIComponent(email)}`));
                if (res.ok) {
                    const data = await res.json();
                    setProfile(data);
                    setUsername(data.username ?? '');
                    setFirstName(data.profileinfo?.firstName ?? '');
                    setLastName(data.profileinfo?.lastName ?? '');
                    setDisplayName(data.username ?? '');
                    setSalesQuota(data.sales_quota ?? '');
                    setRoles(data.roles ?? []);
                    setFunctionalAreas(data.functional_areas ?? []);
                } else {
                    setFetchError('Could not load profile.');
                }
            } catch (err) {
                setFetchError('Error loading profile: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [email]);

    const markDirty = (setter) => (val) => {
        setter(val);
        setIsDirty(true);
        setSaveSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!profile) return;
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            const updated = {
                ...profile,
                username,
                profileinfo: {
                    ...(profile.profileinfo ?? {}),
                    first_name: firstName,
                    last_name: lastName,
                    display_name: displayName,
                },
                sales_quota: salesQuota,
                roles,
                functional_areas: functionalAreas,
            };
            const res = await fetch(apiUrl(`/profile/save`), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updated),
            });
            if (!res.ok) throw new Error('Save failed.');
            const saved = await res.json();
            setProfile(saved);
            setIsDirty(false);
            setSaveSuccess(true);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        if (!profile) return;
        setUsername(profile.username ?? '');
        setFirstName(profile.profileinfo?.first_name ?? '');
        setLastName(profile.profileinfo?.last_name ?? '');
        setDisplayName(profile.profileinfo?.display_name ?? '');
        setSalesQuota(profile.sales_quota ?? '');
        setRoles(profile.roles ?? []);
        setFunctionalAreas(profile.functional_areas ?? []);
        setIsDirty(false);
        setSaveSuccess(false);
        setSaveError(null);
    };

    const formatDate = (val) => {
        if (!val) return '—';
        try { return new Date(val).toLocaleString(); } catch { return val; }
    };

    const fieldCls = "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";
    const readFieldCls = "block w-full rounded-md border-0 py-1.5 bg-gray-50 text-gray-500 shadow-sm ring-1 ring-inset ring-gray-200 sm:text-sm sm:leading-6 cursor-not-allowed";

    return (
        <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
            <Titlebar title="Profile" />

            <div className="m-3">
                {loading && (
                    <div className="py-12 text-center text-sm text-gray-400">Loading profile…</div>
                )}

                {fetchError && !loading && (
                    <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{fetchError}</div>
                )}

                {!loading && !fetchError && (
                    <form onSubmit={handleSubmit} className="space-y-12">

                        {/* ── Account Info ── */}
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Account</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">Core account identifiers. Email and provider are set by your auth provider and cannot be changed here.</p>

                            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                {/* Username */}
                                <div className="sm:col-span-4">
                                    <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">Username</label>
                                    <div className="mt-2">
                                        <input id="username" type="text" value={username}
                                            onChange={(e) => markDirty(setUsername)(e.target.value)}
                                            className={fieldCls} placeholder="username" />
                                    </div>
                                </div>

                                {/* Email – read only */}
                                <div className="sm:col-span-4">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Email</label>
                                    <div className="mt-2">
                                        <input readOnly value={profile?.email ?? ''} className={readFieldCls} />
                                    </div>
                                </div>

                                {/* Provider – read only */}
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Provider</label>
                                    <div className="mt-2">
                                        <input readOnly value={profile?.provider ?? ''} className={readFieldCls} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Profile Info ── */}
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Personal Information</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">Your public-facing profile details.</p>

                            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                {/* Avatar */}
                                {profile?.profileinfo?.avatar_url && (
                                    <div className="col-span-full">
                                        <label className="block text-sm font-medium leading-6 text-gray-900">Photo</label>
                                        <div className="mt-2 flex items-center gap-x-3">
                                            <img src={profile.profileinfo.avatar_url} alt="avatar"
                                                className="h-16 w-16 rounded-full ring-2 ring-gray-200" />
                                        </div>
                                    </div>
                                )}

                                {/* First name */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">First name</label>
                                    <div className="mt-2">
                                        <input id="first-name" type="text" value={firstName}
                                            onChange={(e) => markDirty(setFirstName)(e.target.value)}
                                            autoComplete="given-name" className={fieldCls} />
                                    </div>
                                </div>

                                {/* Last name */}
                                <div className="sm:col-span-3">
                                    <label htmlFor="last-name" className="block text-sm font-medium leading-6 text-gray-900">Last name</label>
                                    <div className="mt-2">
                                        <input id="last-name" type="text" value={lastName}
                                            onChange={(e) => markDirty(setLastName)(e.target.value)}
                                            autoComplete="family-name" className={fieldCls} />
                                    </div>
                                </div>

                                {/* Display name */}
                                <div className="sm:col-span-4">
                                    <label htmlFor="display-name" className="block text-sm font-medium leading-6 text-gray-900">Display name</label>
                                    <div className="mt-2">
                                        <input id="display-name" type="text" value={displayName}
                                            onChange={(e) => markDirty(setDisplayName)(e.target.value)}
                                            className={fieldCls} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Sales Quota ── */}
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Sales Quota</h2>
                            <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label htmlFor="sales-quota" className="block text-sm font-medium leading-6 text-gray-900">sales_quota</label>
                                    <div className="mt-2">
                                        <input
                                            id="sales-quota"
                                            type="number"
                                            value={salesQuota}
                                            readOnly
                                            className={readFieldCls}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Roles ── */}
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Roles</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {isAdmin ? 'Manage the roles assigned to this account.' : 'Your assigned roles (read-only).'}
                            </p>
                            <div className="mt-6">
                                <TagEditor
                                    label="role"
                                    items={roles}
                                    availableOptions={AVAILABLE_ROLES}
                                    onChange={(val) => markDirty(setRoles)(val)}
                                    readOnly={!isAdmin}
                                />
                            </div>
                        </div>

                        {/* ── Functional Areas ── */}
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Functional Areas</h2>
                            <p className="mt-1 text-sm leading-6 text-gray-600">
                                {isAdmin ? 'Manage the functional areas for this account.' : 'Your assigned functional areas (read-only).'}
                            </p>
                            <div className="mt-6">
                                <TagEditor
                                    label="area"
                                    items={functionalAreas}
                                    availableOptions={AVAILABLE_FUNCTIONAL_AREAS}
                                    onChange={(val) => markDirty(setFunctionalAreas)(val)}
                                    readOnly={!isAdmin}
                                />
                            </div>
                        </div>

                        {/* ── Metadata ── */}
                        <div className="border-b border-gray-900/10 pb-12">
                            <h2 className="text-base font-semibold leading-7 text-gray-900">Metadata</h2>
                            <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Created</label>
                                    <div className="mt-2">
                                        <input readOnly value={formatDate(profile?.create_date)} className={readFieldCls} />
                                    </div>
                                </div>
                                <div className="sm:col-span-3">
                                    <label className="block text-sm font-medium leading-6 text-gray-900">Last Modified</label>
                                    <div className="mt-2">
                                        <input readOnly value={formatDate(profile?.modified_date)} className={readFieldCls} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Save bar ── */}
                        <div className="flex items-center justify-between gap-x-4 pb-6">
                            <div className="text-sm">
                                {saveSuccess && <span className="text-green-600 font-medium">Saved successfully.</span>}
                                {saveError && <span className="text-red-600 font-medium">{saveError}</span>}
                            </div>
                            <div className="flex items-center gap-x-3">
                                <button type="button" onClick={handleReset} disabled={!isDirty || isSaving}
                                    className="text-sm font-semibold leading-6 text-gray-700 hover:text-gray-900 disabled:opacity-40">
                                    Reset
                                </button>
                                <button type="submit" disabled={!isDirty || isSaving}
                                    className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-40">
                                    {isSaving ? 'Saving…' : 'Save Changes'}
                                </button>
                            </div>
                        </div>

                    </form>
                )}
            </div>
        </div>
    );
}

export default ProfileForm;
